import React, { useContext, useState, useRef, useEffect, useCallback } from 'react';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import axios from 'axios';
import callHightlightContentAPI from 'components/Dashboard/utilities/callHightlightContentAPI';
import ChangeKeywordsExclusions from 'components/Dashboard/utilities/changeKeywordExclusions';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import highlightWidget from 'components/Dashboard/apiCalls/callHighlightWidget';
import ChangeKeywordsCategory from 'components/Dashboard/utilities/changeKeywordsCategory';
import { SaveButton } from 'components/Dashboard/components/Share';
import { useLocation } from 'react-router-dom';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import { useParams } from 'react-router-dom';

import TagsLists from 'components/Dashboard/TagsList/index';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import ContentTypeBadge from 'components/Dashboard/components/ContentTypeBadge';
import getAppSite from 'utils/getAppSite';
import time from 'utils/time';

export const formatCalendarEventTitle = (title, dateTime) => {
  if (getAppSite() !== 'usa' || !dateTime) return title;

  const eventTime = time(dateTime);
  const isMidnight = eventTime.hour() === 0 && eventTime.minute() === 0 && eventTime.second() === 0;
  if (isMidnight) return title;

  return `${eventTime.format('h:mm A')} ET: ${title}`;
};

const CalendarItem = (props) => {
  const {
    item,
    widgetEmbed,
    keywordListId,
    array,
    index,
    highlightedLists,
    customKeywords,
    hideYellowDot,
    showContentTypeBadge,
    eventLocation,
    singleEvent,
    tagsFixedPosition,
    scrollBarRef,
    zIndex,
    onlyEmbargoedTag,
  } = props;
  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults, lastSeenMarkers, itemsViewed } = useContentResults();
  const { net_api_url, search, referenceState, keywordsLists } = state;
  const { activeReference } = referenceState;
  const { UpcomingEvents } = contentResults;
  const { query, list, activeSearch } = search;
  const [text, setText] = useState('');
  const [loadingHighlight, setLoadingHighlight] = useState(false);
  const [finalTitle, setFinalTitle] = useState(item.title);
  const [heightBody, setHeightBody] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const displayTitle = formatCalendarEventTitle(finalTitle, item.dateTime);

  const isMounted = useRef(null);
  const haveBody = item.body !== '';
  const expandableCondition = haveBody && !showContentTypeBadge;

  const contentHighlightedLists = highlightedLists;

  const eventIdParam = getUrlParam('eventId');
  const mainSource = useRef(null);

  useEffect(() => {
    if (activeSearch || activeReference || widgetEmbed || customKeywords) {
      highlightEvents();
    } else {
      setLoadingHighlight(false);
    }

    const source = new AbortController();
    mainSource.current = source;
    isMounted.current = true;
    return () => {
      source.abort('Event item canceled by the user.');
      isMounted.current = false;
    };
  }, []);

  const highlightEvents = async () => {
    try {
      setLoadingHighlight(true);
      let andKeywords = list?.andKeywords ?? [];
      let queryHighlight = list !== null ? ChangeKeywordsCategory(list.keywords) : query;
      if (activeReference) {
        andKeywords = referenceState.topic?.andKeywords ?? referenceState.andKeywords;
        queryHighlight = ChangeKeywordsCategory(referenceState.topic?.keywords ?? referenceState.keywords);
      }

      if (customKeywords) {
        andKeywords = [];
        queryHighlight = ChangeKeywordsCategory(customKeywords);
      }

      let propertiesHighlight = [
        item.objectID,
        item.type,
        queryHighlight,
        ChangeKeywordsExclusions(andKeywords),
        mainSource.current,
      ];
      let contentHightlighted =
        widgetEmbed && keywordListId
          ? await highlightWidget(item.type, item.objectID, keywordListId)
          : await callHightlightContentAPI(propertiesHighlight);
      setFinalTitle(contentHightlighted?.highlightedTitle ?? item.title);
      setText(contentHightlighted?.highlightedHtmlBody ?? '');
      setLoadingHighlight(false);
    } catch (error) {}
  };

  const feedItem = item?.feedItemFlags;
  const isBackpopulatedCondition = feedItem?.isBackpopulated === null || feedItem?.isBackpopulated === true;
  const unseenConditional =
    list !== null &&
    !!lastSeenMarkers['eventFeedItemId'] &&
    lastSeenMarkers['eventFeedItemId'] < feedItem?.feedItemId &&
    !itemsViewed.includes(item?.objectID) &&
    !isBackpopulatedCondition &&
    !hideYellowDot;

  const location = useLocation();
  const params = useParams();
  const { inboxSection } = useMainSectionParameter(params);
  const { rememberPosition } = useRememberPosition();

  const createContent = async () => {
    if (expandableCondition && text === '') {
      try {
        let content = await axios.get(`${net_api_url}/api/event/${item.id}`);
        const listsIds = contentHighlightedLists?.map((item) => item?.id);
        const listsToHighlight = keywordsLists?.filter((item) => listsIds?.includes(item?.id));
        if (isMounted.current) {
          let text = content.data.description;
          if (highlightedLists?.length > 0) {
            let highlightContent = await callHightlightContentAPI([
              item.objectID,
              item.type,
              ChangeKeywordsCategory(listsToHighlight.flatMap((list) => list.keywords)),
              ChangeKeywordsExclusions(listsToHighlight.flatMap((list) => list.andKeywords)),
              mainSource.current,
            ]);
            text = highlightContent?.highlightedHtmlBody ?? '';
          }
          setText(text);
        }
      } catch (error) {
        if (error.name === 'CanceledError') {
          console.error(error?.config?.signal?.reason);
        } else {
          console.error(error);
        }
      }
    }
  };

  const bodyContainer = useCallback(
    (node) => {
      let calculateHeight = () => {
        if (node !== null) {
          node.style.height = 'auto';
          let nodeHeight = node.getBoundingClientRect().height;
          if (heightBody !== nodeHeight) {
            setHeightBody(nodeHeight);
          }
          if (!isOpen) {
            node.style.height = 0;
          }
        }
      };
      calculateHeight(); /* 
      window.addEventListener('resize', calculateHeight);
      return () => {
        window.removeEventListener('resize', calculateHeight);
      }; */
    },
    [text]
  );

  return (
    <>
      {loadingHighlight && (
        <SkeletonTheme baseColor='#f2f2f2'>
          <div className='my-2 border-bottom'>
            <div className='py-3'>
              <Skeleton height={19} width={'70%'} />
            </div>
          </div>
        </SkeletonTheme>
      )}
      {!loadingHighlight && (
        <div
          className={`${singleEvent ? '' : 'my-2'} calendar-item ${
            unseenConditional ? 'unseen-item calendar-unseen' : ''
          } ${highlightedLists?.length > 0 ? 'item-highlighted' : 'no-highlight'} ${
            eventIdParam && parseInt(eventIdParam) === item?.id ? 'calendar-item-direct-load' : ''
          }`}
          style={{ zIndex: zIndex ?? (array ? array.length - index : 1) }}
          id={`event-${item?.id}`}
        >
          {unseenConditional && (
            <>
              <span className='unseen-dot calendar-unseen' />
            </>
          )}
          <div
            className={`upcoming-event  ${isOpen ? 'upcoming-event-open' : ''} ${
              expandableCondition ? 'upcoming-event-expandable' : ''
            }`}
          >
            <div
              onClick={async () => {
                createContent();
                if (expandableCondition) {
                  setIsOpen(!isOpen);
                }

                if (!!UpcomingEvents && inboxSection) {
                  rememberPosition('UpcomingEvents', null, index);
                }
              }}
              style={{ cursor: expandableCondition ? 'pointer' : 'normal' }}
            >
              <div className={`flex-centered ${showContentTypeBadge ? 'align-items-start' : ''}`}>
                {showContentTypeBadge && (
                  <div className='flex-grow-1'>
                    {singleEvent && (
                      <div className='mt-1 specific-bottom-margin-12 flex-grow-1 text-light-blue blue-icon content-type-badge'>
                        <ContentTypeBadge objectType={'UpcomingEvents'} name={'Upcoming event'} />
                      </div>
                    )}
                    <h4 className={`mb-2 content-item-title font-weight-bold`}>
                      <span dangerouslySetInnerHTML={createMarkup(displayTitle)} />
                    </h4>
                    <div className='content-title'>
                      <p className={`title-body ${singleEvent ? 'mb-0' : ''}`}>{eventLocation}</p>
                    </div>
                  </div>
                )}
                {!showContentTypeBadge && (
                  <h4 className='mb-0 py-3 flex-grow-1'>
                    <span dangerouslySetInnerHTML={createMarkup(displayTitle)} />
                    {expandableCondition && (
                      <button
                        className={`general-button ml-2 calendar-more-button ${!isOpen ? 'calendar-less-button' : ''}`}
                      >
                        {isOpen ? 'less' : 'more'}
                        <span className='icon-arrow-down'></span>
                      </button>
                    )}
                  </h4>
                )}
                {!location.pathname.toLowerCase().startsWith('/embed') && !widgetEmbed && (
                  <div className='flex-centered'>
                    <div className={`pl-lg-2 calendar-save-button-container ${showContentTypeBadge ? 'mr-0' : ''}`}>
                      <SaveButton item={item} listItem />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {expandableCondition && (
            <div className='upcoming-event-body' style={{ height: `${isOpen ? heightBody : 0}px` }} ref={bodyContainer}>
              <div className='p-3 upcoming-event-body-container'>
                {text === '' && <p className='mb-0'>Loading...</p>}
                {text !== '' && <div dangerouslySetInnerHTML={createMarkup(text)}></div>}
              </div>
            </div>
          )}

          {highlightedLists?.length > 0 && (
            <div className='calendar-tag-list'>
              <TagsLists
                feedItemFlags={[{ matchingKeywordLists: contentHighlightedLists }]}
                index={0}
                calendarTags
                tagsFixedPosition={tagsFixedPosition}
                scrollBarRef={scrollBarRef}
                onlyEmbargoedTag={onlyEmbargoedTag}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CalendarItem;
